import { type ComponentType, type ReactElement, useState } from 'react';

import type { KeysOfByType } from '@iconik-io/core/src/types';

import moveItem from '../../utils/move_item';
import {
  DRAG_DIRECTION_AFTER,
  DRAG_DIRECTION_BEFORE,
  type DragDirection,
} from './constants';
import DragSourceDropTarget from './drag_source_drop_target';

export type ItemComponentProps<Item> = {
  item: Item;
  index: number;
};

type Props<Item, OwnProps> = {
  items: Item[];
  // Key property of an item which identifies it among others,
  // usually it's either `id` or `name`.
  itemKeyProperty: KeysOfByType<Item, string>;
  itemComponent: ComponentType<OwnProps>;
  itemComponentProps?: Omit<OwnProps, keyof ItemComponentProps<Item>>;
  onItemsUpdated: (items: Item[]) => void;
  itemWrapperClassName?: string;
};

type DragHoverState = {
  direction: DragDirection;
  dragIndex: number;
  hoverIndex: number;
};

const SortableList = <Item, OwnProps extends ItemComponentProps<Item>>(
  props: Props<Item, OwnProps>,
): ReactElement => {
  const ItemComponent = props.itemComponent;
  const itemKey = props.itemKeyProperty;
  const [dragHoverState, setDragHoverState] = useState<DragHoverState>();

  const onDrop = (): void => {
    if (!dragHoverState) {
      return;
    }

    const { direction, hoverIndex, dragIndex } = dragHoverState;

    let toIndex = hoverIndex;
    if (direction === DRAG_DIRECTION_BEFORE && dragIndex < hoverIndex) {
      toIndex -= 1;
    }
    if (direction === DRAG_DIRECTION_AFTER && dragIndex > hoverIndex) {
      toIndex += 1;
    }

    // do not allow to drop into itself
    if (toIndex === dragIndex) {
      return;
    }

    const updatedItems = moveItem(props.items, dragIndex, toIndex);

    setDragHoverState(undefined);

    props.onItemsUpdated(updatedItems);
  };

  return (
    <>
      {props.items.map((item, index) => (
        <DragSourceDropTarget
          className={props.itemWrapperClassName}
          key={`${item[itemKey]}_${index}`}
          id={item[itemKey] as string}
          index={index}
          direction={dragHoverState?.direction}
          dropSuccess={onDrop}
          moveElement={(dragIndex, hoverIndex, direction) => {
            if (dragIndex === hoverIndex) {
              setDragHoverState(undefined);
              return;
            }
            let updatedDirection = direction;
            if (
              hoverIndex === dragIndex + 1 &&
              direction === DRAG_DIRECTION_BEFORE
            ) {
              updatedDirection = DRAG_DIRECTION_AFTER;
            }
            if (
              hoverIndex === dragIndex - 1 &&
              direction === DRAG_DIRECTION_AFTER
            ) {
              updatedDirection = DRAG_DIRECTION_BEFORE;
            }
            setDragHoverState({
              dragIndex,
              hoverIndex,
              direction: updatedDirection,
            });
          }}
        >
          <ItemComponent
            {...(props.itemComponentProps as OwnProps)}
            item={item}
            index={index}
          />
        </DragSourceDropTarget>
      ))}
    </>
  );
};

export default SortableList;
